import { useEffect, useRef } from "react";

export function useAIRoomSocket(
  socket,
  roomName,
  sessionId,
  {
    setWaitingForPrompts,
    setPlayers,
    setVoteDistributions,
    setSetupDone,
    setMyVote,
    setAIRoomState,
    setAdminSessionId,
    onKicked,
  },
) {
  // Use refs so cleanup always has the latest callbacks without re-registering
  const callbacksRef = useRef({
    setWaitingForPrompts,
    setPlayers,
    setVoteDistributions,
    setSetupDone,
    setMyVote,
    setAIRoomState,
    setAdminSessionId,
    onKicked,
  });
  callbacksRef.current = {
    setWaitingForPrompts,
    setPlayers,
    setVoteDistributions,
    setSetupDone,
    setMyVote,
    setAIRoomState,
    setAdminSessionId,
    onKicked,
  };

  useEffect(() => {
    socket.emit("join room", roomName);

    if (sessionId) {
      socket.emit("ai room reconnect", { roomName, sessionId });
    }

    const onWaiting = () => callbacksRef.current.setWaitingForPrompts(true);
    const onStopWaiting = () =>
      callbacksRef.current.setWaitingForPrompts(false);

    const onRoomState = (data) => {
      if (data.players) callbacksRef.current.setPlayers(data.players);
      if (data.voteDistributions)
        callbacksRef.current.setVoteDistributions(data.voteDistributions);
      if (data.setupDone !== undefined)
        callbacksRef.current.setSetupDone(data.setupDone);
      if (data.adminSessionId !== undefined)
        callbacksRef.current.setAdminSessionId(data.adminSessionId);
    };

    const onYourState = (data) => {
      if (data.myVote) callbacksRef.current.setMyVote(data.myVote);
    };

    const onFirstPrompts = () => {
      callbacksRef.current.setAIRoomState("done");
    };

    const onKickedEvent = (data) => {
      if (callbacksRef.current.onKicked) callbacksRef.current.onKicked(data);
    };

    socket.on("waitingForPrompts", onWaiting);
    socket.on("stopWaitingForPrompts", onStopWaiting);
    socket.on("ai room state", onRoomState);
    socket.on("ai room your state", onYourState);
    socket.on("firstPromptsGenerated", onFirstPrompts);
    socket.on("ai room kicked", onKickedEvent);

    return () => {
      socket.off("waitingForPrompts", onWaiting);
      socket.off("stopWaitingForPrompts", onStopWaiting);
      socket.off("ai room state", onRoomState);
      socket.off("ai room your state", onYourState);
      socket.off("firstPromptsGenerated", onFirstPrompts);
      socket.off("ai room kicked", onKickedEvent);
    };
  }, [socket, roomName, sessionId]);
}
